import { useState, useEffect, useCallback } from 'react'
import { supabase } from './supabase'
import { syncOrderFromDeliveryJob } from './fulfillment'

// ─── AUTH ─────────────────────────────────────────────────────────

export function useDriverAuth() {
  const [user, setUser] = useState<any>(null)
  const [driverId, setDriverId] = useState<number | null>(null)
  const [loading, setLoading] = useState(true)
  const [resolveError, setResolveError] = useState<string | null>(null)
  const [authDebug, setAuthDebug] = useState<{
    userId: string | null
    rpcResult: any
    rpcError: any
  }>({ userId: null, rpcResult: null, rpcError: null })

  useEffect(() => {
    const fetchSession = async () => {
      const {
        data: { session },
      } = await supabase.auth.getSession()
      const u = session?.user || null
      setUser(u)
      console.log('[DRIVER-AUTH] Session user:', u?.id || 'none')
      setAuthDebug((prev) => ({ ...prev, userId: u?.id || null }))
      if (u) {
        await fetchDriverId(u.id)
      } else {
        setLoading(false)
      }
    }

    fetchSession()

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      const u = session?.user || null
      setUser(u)
      setAuthDebug((prev) => ({ ...prev, userId: u?.id || null }))
      if (u) {
        fetchDriverId(u.id)
      } else {
        setDriverId(null)
        setLoading(false)
      }
    })

    return () => subscription.unsubscribe()
  }, [])

  const fetchDriverId = async (authUserId?: string) => {
    setResolveError(null)
    let resolvedDriverId: number | null = null
    let hadNetworkError = false

    // Get auth user id if not passed
    let userId = authUserId
    if (!userId) {
      const {
        data: { user: authUser },
      } = await supabase.auth.getUser()
      userId = authUser?.id || null
    }
    console.log('[DRIVER-AUTH] Auth user id:', userId)

    if (!userId) {
      console.error('[DRIVER-AUTH] ❌ No authenticated user')
      setLoading(false)
      return
    }

    try {
      // ═══ STEP A: Try RPC current_driver_id ═══
      console.log('[DRIVER-AUTH] Step A: Calling current_driver_id RPC...')
      const { data: rpcData, error: rpcError } =
        await supabase.rpc('current_driver_id')
      console.log('[DRIVER-AUTH] RPC current_driver_id raw response:', {
        data: rpcData,
        error: rpcError,
      })

      setAuthDebug((prev) => ({
        ...prev,
        rpcResult: rpcData,
        rpcError: rpcError,
      }))

      if (!rpcError && rpcData) {
        console.log('[DRIVER-AUTH] ✅ RPC returned driver_id:', rpcData)
        resolvedDriverId = rpcData
      } else {
        if (rpcError) {
          console.warn('[DRIVER-AUTH] ⚠️ RPC failed:', rpcError.message)
          // RPC failure could be temporary (missing function, network) — continue to fallback
          hadNetworkError = true
        } else {
          console.warn('[DRIVER-AUTH] ⚠️ RPC returned null/empty')
        }
      }

      // ═══ STEP B: Fallback — direct query on driver_users ═══
      if (!resolvedDriverId) {
        console.log(
          '[DRIVER-AUTH] Step B: Querying driver_users for user_id:',
          userId,
        )
        const { data: driverLink, error: linkError } = await supabase
          .from('driver_users')
          .select('driver_id, is_active')
          .eq('user_id', userId)
          .eq('is_active', true)
          .limit(1)
          .maybeSingle()

        console.log('[DRIVER-AUTH] driver_users fallback response:', {
          data: driverLink,
          error: linkError,
        })

        if (linkError) {
          console.warn(
            '[DRIVER-AUTH] ⚠️ driver_users query error:',
            linkError.message,
          )
          hadNetworkError = true

          // Try without is_active filter as last resort
          console.log(
            '[DRIVER-AUTH] Step B2: Querying driver_users without is_active filter...',
          )
          const { data: anyLink, error: anyError } = await supabase
            .from('driver_users')
            .select('*')
            .eq('user_id', userId)
            .limit(5)

          console.log('[DRIVER-AUTH] driver_users (unfiltered) result:', {
            data: anyLink,
            error: anyError,
          })

          if (!anyError && anyLink && anyLink.length > 0) {
            console.log(
              '[DRIVER-AUTH] 📋 driver_users columns:',
              Object.keys(anyLink[0]),
            )
            const link = anyLink[0]
            const fallbackId = link.driver_id || link.driverId || link.id
            if (fallbackId) {
              console.log(
                '[DRIVER-AUTH] ✅ Resolved driver_id from unfiltered query:',
                fallbackId,
              )
              resolvedDriverId = fallbackId
              hadNetworkError = false
            }
          } else if (anyError) {
            hadNetworkError = true
          }
        } else if (driverLink && driverLink.driver_id) {
          console.log(
            '[DRIVER-AUTH] ✅ Fallback resolved driver_id:',
            driverLink.driver_id,
          )
          resolvedDriverId = driverLink.driver_id
        } else {
          console.log('[DRIVER-AUTH] driver_users returned no active row')
        }
      }

      // ═══ STEP C: Verify driver record exists ═══
      if (resolvedDriverId) {
        console.log(
          '[DRIVER-AUTH] Step C: Fetching driver record for id:',
          resolvedDriverId,
        )
        const { data: driverRecord, error: driverError } = await supabase
          .from('drivers')
          .select('id, full_name, is_active')
          .eq('id', resolvedDriverId)
          .maybeSingle()

        console.log('[DRIVER-AUTH] Driver record fetch result:', {
          data: driverRecord,
          error: driverError,
        })

        if (driverError) {
          console.warn(
            '[DRIVER-AUTH] ⚠️ Driver record fetch error:',
            driverError.message,
          )
          // Still use the resolved ID — the driver exists, we just couldn't verify
          hadNetworkError = true
        }

        if (driverRecord) {
          console.log(
            '[DRIVER-AUTH] ✅ Driver verified:',
            driverRecord.full_name,
            '(active:',
            driverRecord.is_active,
            ')',
          )
        } else if (!driverError) {
          // No error but no record — driver_id points to nothing
          console.error(
            '[DRIVER-AUTH] ❌ driver_id',
            resolvedDriverId,
            'has no matching drivers row',
          )
          resolvedDriverId = null
        }
      }

      // ═══ FINAL: Set state ═══
      console.log('[DRIVER-AUTH] ═══ RESOLUTION COMPLETE ═══')
      console.log('[DRIVER-AUTH] Resolved driver_id:', resolvedDriverId)
      console.log('[DRIVER-AUTH] Had network errors:', hadNetworkError)

      if (resolvedDriverId) {
        setDriverId(resolvedDriverId)
        setResolveError(null)
        setAuthDebug((prev) => ({
          ...prev,
          rpcResult: `resolved:${resolvedDriverId}`,
          rpcError: null,
        }))
      } else if (hadNetworkError) {
        // Temporary error — don't show "Sin perfil", show retryable error
        console.warn(
          '[DRIVER-AUTH] ⚠️ Could not resolve driver due to temporary errors — NOT showing no-profile screen',
        )
        setResolveError(
          'Error de conexión al verificar tu perfil. Intenta de nuevo.',
        )
        setAuthDebug((prev) => ({ ...prev, rpcError: 'temporary_error' }))
      } else {
        // Definitive: both RPC and direct query returned no results (no errors)
        console.error(
          '[DRIVER-AUTH] ❌ DEFINITIVE: No driver profile found for user',
          userId,
        )
        console.error(
          '[DRIVER-AUTH] Reason: RPC returned null AND driver_users has no active row for this user',
        )
        setDriverId(null)
        setResolveError(null)
        setAuthDebug((prev) => ({ ...prev, rpcError: 'no_driver_profile' }))
      }
    } catch (err) {
      console.error('[DRIVER-AUTH] ❌ Exception fetching driver id:', err)
      // Exception = temporary error, don't show "Sin perfil"
      setResolveError('Error inesperado al cargar tu perfil. Intenta de nuevo.')
      setAuthDebug((prev) => ({ ...prev, rpcError: String(err) }))
    } finally {
      setLoading(false)
    }
  }

  const retry = useCallback(async () => {
    setLoading(true)
    setResolveError(null)
    await fetchDriverId()
  }, [])

  const logout = async () => {
    await supabase.auth.signOut()
  }

  return { user, driverId, loading, resolveError, logout, authDebug, retry }
}

// ─── SESSION & LOCATION ───────────────────────────────────────────

export function useDriverSession(driverId: number | null) {
  const [isOnline, setIsOnline] = useState(false)
  const [location, setLocation] = useState<{ lat: number; lng: number } | null>(
    null,
  )

  const toggleStatus = async (online: boolean) => {
    if (!driverId) return
    try {
      if (online) {
        const { error } = await supabase.rpc('set_driver_online_secure')
        if (error) throw error
      } else {
        const { error } = await supabase.rpc('set_driver_offline_secure')
        if (error) throw error
      }
      setIsOnline(online)
    } catch (err) {
      console.error('[DRIVER] Error updating status:', err)
    }
  }

  // Location Tracking via secure RPC
  useEffect(() => {
    if (!isOnline || !driverId) return

    const updateLocation = async (lat: number, lng: number) => {
      setLocation({ lat, lng })
      try {
        await supabase.rpc('update_driver_location_secure', {
          p_lat: lat,
          p_lng: lng,
        })
      } catch (err) {
        console.error('[DRIVER] Location update error:', err)
      }
    }

    const interval = setInterval(() => {
      if ('geolocation' in navigator) {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            updateLocation(position.coords.latitude, position.coords.longitude)
          },
          (error) => console.error('[DRIVER] GPS Error:', error),
          { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 },
        )
      }
    }, 15000)

    // Initial fetch
    if ('geolocation' in navigator) {
      navigator.geolocation.getCurrentPosition((position) => {
        updateLocation(position.coords.latitude, position.coords.longitude)
      })
    }

    return () => clearInterval(interval)
  }, [isOnline, driverId])

  return { isOnline, toggleStatus, location }
}

// ─── OFFERS ───────────────────────────────────────────────────────

export function useDriverOffers(driverId: number | null) {
  const [offers, setOffers] = useState<any[]>([])
  const [debugInfo, setDebugInfo] = useState<{
    lastFetchTime: string | null
    pendingCount: number
    allOffersCount: number
    bypassOffers: any[]
    lastError: string | null
    realtimeActive: boolean
  }>({
    lastFetchTime: null,
    pendingCount: 0,
    allOffersCount: 0,
    bypassOffers: [],
    lastError: null,
    realtimeActive: false,
  })

  const fetchOffers = useCallback(async () => {
    if (!driverId) return
    const now = new Date().toISOString()
    console.log(
      '[DRIVER] 📡 Fetching offers for driver_id:',
      driverId,
      'at:',
      now,
    )

    // PRIMARY QUERY: pending + unexpired — use * for delivery_jobs to avoid column name mismatches
    const { data, error } = await supabase
      .from('delivery_job_offers')
      .select(
        `
        id,
        delivery_job_id,
        driver_id,
        status,
        expires_at,
        created_at,
        delivery_jobs (*)
      `,
      )
      .eq('driver_id', driverId)
      .eq('status', 'pending')
      .gt('expires_at', now)
      .order('created_at', { ascending: false })

    // BYPASS QUERY: ALL offers for this driver, no filters (to diagnose RLS/expiry)
    const { data: allOffers, error: allError } = await supabase
      .from('delivery_job_offers')
      .select('id, status, expires_at, created_at, delivery_job_id')
      .eq('driver_id', driverId)
      .order('created_at', { ascending: false })
      .limit(10)

    if (error) {
      console.error('[DRIVER] ❌ Error fetching offers:', error)
      console.error('[DRIVER] Error details:', JSON.stringify(error, null, 2))
      setDebugInfo((prev) => ({
        ...prev,
        lastFetchTime: now,
        pendingCount: 0,
        lastError: error.message || JSON.stringify(error),
        allOffersCount: allOffers?.length || 0,
        bypassOffers: allOffers || [],
      }))
    } else {
      const pendingCount = data?.length || 0
      console.log(
        `[DRIVER] ✅ Fetched ${pendingCount} pending offers (now=${now})`,
      )

      if (data && data.length > 0) {
        data.forEach((offer: any) => {
          const expiresIn = Math.round(
            (new Date(offer.expires_at).getTime() - Date.now()) / 1000,
          )
          console.log(
            `[DRIVER]   Offer #${offer.id}: job=${offer.delivery_job_id}, expires_in=${expiresIn}s, has_job_data=${!!offer.delivery_jobs}`,
          )
          // Log real delivery_jobs columns to discover schema
          if (offer.delivery_jobs) {
            console.log(
              '[DRIVER]   📋 delivery_jobs REAL COLUMNS:',
              Object.keys(offer.delivery_jobs),
            )
            console.log(
              '[DRIVER]   📋 delivery_jobs RAW DATA:',
              JSON.stringify(offer.delivery_jobs, null, 2),
            )
          }
        })
      }

      // Log bypass results
      if (allOffers && allOffers.length > 0) {
        console.log(
          `[DRIVER] 🔍 BYPASS: ${allOffers.length} total offers (all statuses):`,
        )
        allOffers.forEach((o: any) => {
          const expired = new Date(o.expires_at).getTime() < Date.now()
          console.log(
            `[DRIVER]   #${o.id}: status=${o.status}, expired=${expired}, expires_at=${o.expires_at}`,
          )
        })
      } else {
        console.log('[DRIVER] 🔍 BYPASS: 0 offers returned (RLS is blocking)')
        if (allError) {
          console.error('[DRIVER] BYPASS error:', allError)
        }
      }

      setDebugInfo((prev) => ({
        ...prev,
        lastFetchTime: now,
        pendingCount,
        lastError: null,
        allOffersCount: allOffers?.length || 0,
        bypassOffers: allOffers || [],
      }))
    }

    if (!error && data) {
      setOffers(data)
    }
  }, [driverId])

  useEffect(() => {
    fetchOffers()

    if (!driverId) return

    // Poll every 10 seconds as backup
    const pollInterval = setInterval(fetchOffers, 10000)

    const channel = supabase
      .channel(`driver_offers_${driverId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'delivery_job_offers',
          filter: `driver_id=eq.${driverId}`,
        },
        () => {
          console.log(
            '[DRIVER] 🔔 Realtime event on delivery_job_offers — refetching',
          )
          fetchOffers()
        },
      )
      .subscribe((status) => {
        console.log('[DRIVER] 📡 Realtime subscription status:', status)
        setDebugInfo((prev) => ({
          ...prev,
          realtimeActive: status === 'SUBSCRIBED',
        }))
      })

    return () => {
      clearInterval(pollInterval)
      supabase.removeChannel(channel)
    }
  }, [driverId, fetchOffers])

  // Accept uses offer_id (not delivery_job_id) via secure RPC
  const acceptOffer = async (offerId: number) => {
    // Find the offer in current list for logging
    const currentOffer = offers.find((o: any) => o.id === offerId)
    console.log('[DRIVER-ACCEPT] ═══════════════════════════════════')
    console.log('[DRIVER-ACCEPT] Offer ID:', offerId)
    console.log(
      '[DRIVER-ACCEPT] Delivery Job ID:',
      currentOffer?.delivery_job_id || 'unknown',
    )
    console.log('[DRIVER-ACCEPT] Driver ID:', driverId)

    try {
      const { data: rpcData, error } = await supabase.rpc(
        'accept_delivery_offer_secure',
        {
          p_offer_id: offerId,
        },
      )

      console.log('[DRIVER-ACCEPT] RPC raw response:', { data: rpcData, error })
      console.log('[DRIVER-ACCEPT] RPC success flag:', rpcData?.success)

      // Case 1: Supabase-level error (network, auth, SQL exception)
      if (error) {
        console.error('[DRIVER-ACCEPT] ❌ RPC SUPABASE ERROR')
        console.error('[DRIVER-ACCEPT] Error message:', error.message)
        console.error('[DRIVER-ACCEPT] Error code:', error.code)
        console.error('[DRIVER-ACCEPT] Error hint:', (error as any).hint)
        console.error('[DRIVER-ACCEPT] Error details:', (error as any).details)
        console.error('[DRIVER-ACCEPT] Full error:', JSON.stringify(error))
        return { success: false, error: error.message }
      }

      // Case 2: RPC returned data but success === false (backend rejection)
      if (rpcData && rpcData.success === false) {
        const backendError =
          rpcData.error || rpcData.message || 'El servidor rechazó la solicitud'
        console.error('[DRIVER-ACCEPT] ❌ RPC BACKEND REJECTION:', backendError)
        return { success: false, error: backendError }
      }

      // Case 3: Success — either rpcData.success === true or no error
      console.log('[DRIVER-ACCEPT] ✅ RPC succeeded')

      // Fetch the offer to get delivery_job_id for syncing
      const { data: offer } = await supabase
        .from('delivery_job_offers')
        .select('delivery_job_id, delivery_jobs(*)')
        .eq('id', offerId)
        .single()

      if (offer?.delivery_jobs) {
        const job = offer.delivery_jobs as any
        console.log('[DRIVER-ACCEPT] 🔄 Syncing order after offer acceptance')
        await syncOrderFromDeliveryJob(offer.delivery_job_id, 'assigned')

        const { addOrderLog } = await import('./fulfillment')
        await addOrderLog(job.order_id, job.restaurant_id, 'driver_assigned', {
          offer_id: offerId,
          delivery_job_id: offer.delivery_job_id,
          driver_id: driverId,
        })
      }

      // Only fetch offers AFTER confirmed success
      await fetchOffers()
      console.log('[DRIVER-ACCEPT] ═══════════════════════════════════')
      return { success: true }
    } catch (err: any) {
      console.error('[DRIVER-ACCEPT] ❌ Exception:', err)
      console.error(
        '[DRIVER-ACCEPT] Offer ID:',
        offerId,
        'Driver ID:',
        driverId,
      )
      // DO NOT call fetchOffers — keep the current offer visible for retry
      return { success: false, error: err.message }
    }
  }

  const rejectOffer = async (offerId: number) => {
    try {
      const { error } = await supabase
        .from('delivery_job_offers')
        .update({ status: 'rejected' })
        .eq('id', offerId)
      if (error) throw error
      await fetchOffers()
      return { success: true }
    } catch (err: any) {
      console.error('[DRIVER] Error rejecting offer:', err)
      return { success: false, error: err.message }
    }
  }

  return { offers, acceptOffer, rejectOffer, debugInfo, refetch: fetchOffers }
}

// ─── ACTIVE TRIP ──────────────────────────────────────────────────

export function useActiveTrip(driverId: number | null) {
  const [activeTrip, setActiveTrip] = useState<any>(null)

  const fetchActiveTrip = useCallback(async () => {
    if (!driverId) {
      console.log('[DRIVER-TRIP] fetchActiveTrip skipped — no driverId')
      return
    }
    console.log('[DRIVER-TRIP] 🔍 Fetching active trip for driver:', driverId)
    const { data, error } = await supabase
      .from('delivery_jobs')
      .select('*')
      .eq('driver_id', driverId)
      .in('status', ['assigned', 'arrived_pickup', 'picked_up', 'en_route'])
      .order('updated_at', { ascending: false })
      .limit(1)
      .maybeSingle()

    console.log('[DRIVER-TRIP] 🔍 Active trip fetch response:', {
      found: !!data,
      jobId: data?.id || null,
      status: data?.status || null,
      error: error?.message || null,
    })

    if (!error) {
      if (data) {
        console.log(
          '[DRIVER-TRIP] ✅ Active trip detected — id:',
          data.id,
          'status:',
          data.status,
        )
      } else {
        console.log('[DRIVER-TRIP] No active trip found')
      }
      setActiveTrip(data)
    } else {
      console.error('[DRIVER-TRIP] ❌ Active trip fetch error:', error.message)
    }
  }, [driverId])

  // Immediately clear trip from local state (optimistic)
  const clearTrip = useCallback(() => {
    console.log('[DRIVER-TRIP] 🧹 Clearing activeTrip from local state')
    setActiveTrip(null)
  }, [])

  // Expose refetch for external callers (e.g. after accepting an offer)
  const refetchTrip = useCallback(async () => {
    console.log('[DRIVER-TRIP] 🔄 Manual refetchTrip triggered')
    await fetchActiveTrip()
  }, [fetchActiveTrip])

  useEffect(() => {
    fetchActiveTrip()

    if (!driverId) return

    const channel = supabase
      .channel(`driver_trip_${driverId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'delivery_jobs',
          filter: `driver_id=eq.${driverId}`,
        },
        () => {
          fetchActiveTrip()
        },
      )
      .subscribe()

    return () => {
      supabase.removeChannel(channel)
    }
  }, [driverId, fetchActiveTrip])

  // All status actions: try secure RPC first, fallback to direct DB update
  const updateTripStatus = async (
    action: 'arrived' | 'picked_up' | 'en_route' | 'delivered',
    jobId: number,
  ) => {
    const rpcMap: Record<string, string> = {
      arrived: 'mark_arrived_pickup_secure',
      picked_up: 'mark_picked_up_secure',
      en_route: 'mark_en_route_secure',
      delivered: 'mark_delivered_secure',
    }

    const dbStatusMap: Record<string, string> = {
      arrived: 'arrived_pickup',
      picked_up: 'picked_up',
      en_route: 'en_route',
      delivered: 'delivered',
    }

    const syncStatusMap: Record<string, string> = {
      arrived: 'arrived_pickup',
      picked_up: 'picked_up',
      en_route: 'out_for_delivery',
      delivered: 'delivered',
    }

    const rpcName = rpcMap[action]
    const targetDbStatus = dbStatusMap[action]
    const syncStatus = syncStatusMap[action]

    console.log('[DRIVER-TRIP] ═══════════════════════════════════')
    console.log('[DRIVER-TRIP] Action:', action)
    console.log('[DRIVER-TRIP] Job ID:', jobId)
    console.log('[DRIVER-TRIP] RPC:', rpcName)
    console.log('[DRIVER-TRIP] Target DB status:', targetDbStatus)
    console.log('[DRIVER-TRIP] Sync order status:', syncStatus)

    // Step 1: Read current delivery_job state
    const { data: currentJob, error: readError } = await supabase
      .from('delivery_jobs')
      .select('id, status, driver_id, order_id, restaurant_id')
      .eq('id', jobId)
      .single()

    console.log('[DRIVER-TRIP] Current job state:', JSON.stringify(currentJob))
    if (readError) {
      console.error('[DRIVER-TRIP] ❌ Error reading job:', readError)
      return { success: false, error: `Cannot read job: ${readError.message}` }
    }
    if (!currentJob) {
      console.error('[DRIVER-TRIP] ❌ Job not found')
      return { success: false, error: 'Job not found' }
    }

    // Step 2: Try secure RPC first
    let dbUpdated = false
    console.log(`[DRIVER-TRIP] Step 2: Calling RPC ${rpcName}(${jobId})...`)
    const { data: rpcData, error: rpcError } = await supabase.rpc(rpcName, {
      p_delivery_job_id: jobId,
    })

    if (rpcError) {
      console.warn(`[DRIVER-TRIP] ⚠️ RPC ${rpcName} failed:`, rpcError.message)
      console.warn('[DRIVER-TRIP] RPC error code:', rpcError.code)
      console.warn('[DRIVER-TRIP] RPC error hint:', (rpcError as any).hint)
      console.warn(
        '[DRIVER-TRIP] RPC error details:',
        (rpcError as any).details,
      )
      console.warn('[DRIVER-TRIP] RPC full error:', JSON.stringify(rpcError))
    } else {
      console.log(`[DRIVER-TRIP] ✅ RPC ${rpcName} succeeded, data:`, rpcData)
      dbUpdated = true
    }

    // Step 3: Verify the RPC actually updated the row
    if (dbUpdated) {
      const { data: checkJob } = await supabase
        .from('delivery_jobs')
        .select('id, status')
        .eq('id', jobId)
        .single()
      console.log('[DRIVER-TRIP] Post-RPC verification:', checkJob?.status)
      if (checkJob?.status !== targetDbStatus) {
        console.warn(
          `[DRIVER-TRIP] ⚠️ RPC returned success but status is still '${checkJob?.status}', not '${targetDbStatus}'`,
        )
        dbUpdated = false
      }
    }

    // Step 4: Fallback — direct DB update with .select() to verify rows affected
    if (!dbUpdated) {
      console.log(
        `[DRIVER-TRIP] Step 4: Fallback direct update → status='${targetDbStatus}' WHERE id=${jobId} AND driver_id=${currentJob.driver_id}`,
      )
      const { data: updateResult, error: updateError } = await supabase
        .from('delivery_jobs')
        .update({ status: targetDbStatus })
        .eq('id', jobId)
        .eq('driver_id', currentJob.driver_id)
        .select('id, status')

      console.log(
        '[DRIVER-TRIP] Fallback update result:',
        JSON.stringify(updateResult),
      )
      console.log(
        '[DRIVER-TRIP] Fallback update error:',
        updateError ? JSON.stringify(updateError) : 'none',
      )

      if (updateError) {
        console.error(
          '[DRIVER-TRIP] ❌ Fallback update error:',
          updateError.message,
        )
      }

      if (
        updateResult &&
        updateResult.length > 0 &&
        updateResult[0].status === targetDbStatus
      ) {
        console.log('[DRIVER-TRIP] ✅ Fallback update confirmed')
        dbUpdated = true
      } else {
        console.warn(
          '[DRIVER-TRIP] ⚠️ Fallback returned 0 rows or wrong status — trying without driver_id filter...',
        )

        // Step 4b: Try without driver_id filter (in case driver_id column type mismatch)
        const { data: retryResult, error: retryError } = await supabase
          .from('delivery_jobs')
          .update({ status: targetDbStatus })
          .eq('id', jobId)
          .select('id, status')

        console.log(
          '[DRIVER-TRIP] Retry update result:',
          JSON.stringify(retryResult),
        )
        console.log(
          '[DRIVER-TRIP] Retry update error:',
          retryError ? JSON.stringify(retryError) : 'none',
        )

        if (
          retryResult &&
          retryResult.length > 0 &&
          retryResult[0].status === targetDbStatus
        ) {
          console.log('[DRIVER-TRIP] ✅ Retry update confirmed')
          dbUpdated = true
        } else {
          console.error('[DRIVER-TRIP] ❌ ALL UPDATE METHODS FAILED')
          console.error('[DRIVER-TRIP] This is likely an RLS issue. Check:')
          console.error('[DRIVER-TRIP]   1. delivery_jobs UPDATE policy exists')
          console.error(
            '[DRIVER-TRIP]   2. driver_users row exists for auth.uid()',
          )
          console.error('[DRIVER-TRIP]   3. delivery_jobs.driver_id matches')
          return {
            success: false,
            error: `Failed to update status to '${targetDbStatus}'. RPC error: ${rpcError?.message || 'none'}. Check console for details.`,
          }
        }
      }
    }

    // Step 5: Final verification
    const { data: finalJob } = await supabase
      .from('delivery_jobs')
      .select('id, status, driver_id')
      .eq('id', jobId)
      .single()

    console.log('[DRIVER-TRIP] Final verified status:', finalJob?.status)

    if (finalJob?.status !== targetDbStatus) {
      console.error(
        `[DRIVER-TRIP] ❌ CRITICAL: Final status '${finalJob?.status}' != expected '${targetDbStatus}'`,
      )
      return {
        success: false,
        error: `Status stuck at '${finalJob?.status}', expected '${targetDbStatus}'`,
      }
    }

    // Step 6: Sync order status via fulfillment
    console.log('[DRIVER-TRIP] Step 6: Syncing order status...')
    try {
      await syncOrderFromDeliveryJob(jobId, syncStatus)
      console.log('[DRIVER-TRIP] ✅ Order sync completed')
    } catch (syncErr) {
      console.error('[DRIVER-TRIP] ❌ Order sync failed:', syncErr)
    }

    // Step 7: If delivered, immediately clear local state + mark driver available
    if (action === 'delivered') {
      console.log(
        '[DRIVER-TRIP] 📦 Delivery complete — clearing trip immediately',
      )
      setActiveTrip(null) // Optimistic: clear NOW, don't wait for re-fetch
      try {
        await supabase.rpc('set_driver_online_secure')
      } catch (e) {
        console.warn('[DRIVER-TRIP] Could not reset driver status:', e)
      }
    }

    // Step 8: Refresh active trip state (for non-delivered actions)
    if (action !== 'delivered') {
      console.log('[DRIVER-TRIP] Step 8: Refreshing active trip...')
      await fetchActiveTrip()
    }
    console.log('[DRIVER-TRIP] ═══════════════════════════════════')

    return { success: true, method: dbUpdated ? 'verified' : 'unverified' }
  }

  return { activeTrip, updateTripStatus, clearTrip, refetchTrip }
}
